import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import RobotIcon from "@mui/icons-material/SmartToy";
import styles from "./AddActivityModalStyles";
import QuestionnaireModal from "../QuestionnaireTab/QuestionnaireModal";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";

interface AddActivityModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  onActivityAdded: (activity: any) => void;
  refreshMembers: () => void;
}

interface Questionnaire {
  id: number;
  title: string;
}

const AddActivityModal: React.FC<AddActivityModalProps> = ({
  open,
  onClose,
  memberId,
  onActivityAdded,
  refreshMembers,
}) => {
  const [activityType, setActivityType] = useState("call");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<
    number | null
  >(null);
  const [isQuestionnaireModalOpen, setIsQuestionnaireModalOpen] =
    useState(false);
  const [questionnaireStatus, setQuestionnaireStatus] = useState<
    "not_started" | "incomplete" | "complete"
  >("not_started");
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<{
    [key: number]: string;
  }>({});
  const [questionnaireResponseId, setQuestionnaireResponseId] = useState<
    string | null
  >(null);
  const [questionnaireModalKey, setQuestionnaireModalKey] = useState(0);

  const fetchQuestionnaires = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/questionnaires`
      );
      setQuestionnaires(response.data);
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
    }
  }, [memberId]);

  useEffect(() => {
    if (open) {
      fetchQuestionnaires();
    }
  }, [open, fetchQuestionnaires]);

  const handleSubmit = async () => {
    if (selectedQuestionnaire && questionnaireStatus !== "complete") {
      alert("Please complete the questionnaire before submitting.");
      return;
    }

    try {
      const activityData = {
        activityType,
        title,
        notes,
        questionnaireId: selectedQuestionnaire,
        questionnaireResponses:
          questionnaireStatus === "complete"
            ? Object.entries(questionnaireAnswers).map(
                ([questionId, answer]) => ({
                  questionId: parseInt(questionId),
                  answer,
                })
              )
            : [],
        questionnaireResponseId: questionnaireResponseId || uuidv4(),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/members/${memberId}/activities`,
        activityData
      );

      onActivityAdded(response.data);
      if (questionnaireStatus === "complete") {
        refreshMembers();
      }
      handleClose();
    } catch (error) {
      console.error("Error adding activity:", error);
      alert("Error adding activity. Please try again.");
    }
  };

  const handleClose = useCallback(() => {
    setActivityType("call");
    setTitle("");
    setNotes("");
    setSelectedQuestionnaire(null);
    setQuestionnaireStatus("not_started");
    setQuestionnaireAnswers({});
    setQuestionnaireResponseId(null);
    onClose();
  }, [onClose]);

  const handleOpenQuestionnaireModal = useCallback(() => {
    if (selectedQuestionnaire) {
      setQuestionnaireModalKey((prevKey) => prevKey + 1);
      setIsQuestionnaireModalOpen(true);
    }
  }, [selectedQuestionnaire]);

  const handleCloseQuestionnaireModal = useCallback(() => {
    setIsQuestionnaireModalOpen(false);
  }, []);

  const handleQuestionnaireChange = useCallback(
    (
      isComplete: boolean,
      answers: { [key: number]: string },
      responseId?: string | null
    ) => {
      const newStatus = isComplete
        ? "complete"
        : Object.keys(answers).length > 0
        ? "incomplete"
        : "not_started";

      setQuestionnaireStatus(newStatus);
      setQuestionnaireAnswers(answers);
      setQuestionnaireResponseId(responseId || null);
    },
    []
  );

  const handleClearQuestionnaire = useCallback(() => {
    setSelectedQuestionnaire(null);
    setQuestionnaireStatus("not_started");
    setQuestionnaireAnswers({});
    setQuestionnaireResponseId(null);
  }, []);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={styles.modal}>
          <Typography variant="h6" sx={styles.title}>
            Add New Activity Record
          </Typography>
          <FormControl fullWidth sx={styles.formControl}>
            <InputLabel>Activity Type</InputLabel>
            <Select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value as string)}
            >
              <MenuItem value="call">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Call
              </MenuItem>
              <MenuItem value="bi-monthly">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Bi-Monthly Follow Up
              </MenuItem>
              <MenuItem value="bot-call">
                <RobotIcon sx={{ marginRight: 1 }} />
                Bot Call
              </MenuItem>
              <MenuItem value="initial-screening-telehealth-call-scheduling-txt">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Initial screening telehealth call scheduling TXT
              </MenuItem>
              <MenuItem value="initial-screening-telehealth-call">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Initial screening telehealth call
              </MenuItem>
              <MenuItem value="1st-intake-scheduling-txt">
                <PhoneIcon sx={{ marginRight: 1 }} />
                1st intake scheduling TXT
              </MenuItem>
              <MenuItem value="1st-intake-call">
                <PhoneIcon sx={{ marginRight: 1 }} />
                1st intake call
              </MenuItem>
              <MenuItem value="1st-benefit-recommendation-bot-call">
                <RobotIcon sx={{ marginRight: 1 }} />
                1st Benefit recommendation BOT call
              </MenuItem>
              <MenuItem value="1st-benefit-recommendation-txt">
                <PhoneIcon sx={{ marginRight: 1 }} />
                1st Benefit recommendation TXT (Links)
              </MenuItem>
              <MenuItem value="bi-weekly-bot-call">
                <RobotIcon sx={{ marginRight: 1 }} />
                Bi-weekly BOT call
              </MenuItem>
              <MenuItem value="middle-review-call-scheduling-txt">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Middle Review call scheduling TXT
              </MenuItem>
              <MenuItem value="middle-review-call">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Middle review call
              </MenuItem>
              <MenuItem value="conclusion-call-scheduling-txt">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Conclusion call scheduling TXT
              </MenuItem>
              <MenuItem value="conclusion-call">
                <PhoneIcon sx={{ marginRight: 1 }} />
                Conclusion call
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            sx={styles.textField}
          />
          <FormControl fullWidth sx={styles.formControl}>
            <InputLabel>Questionnaire</InputLabel>
            <Select
              value={selectedQuestionnaire || ""}
              onChange={(e) => {
                const value = e.target.value as number;
                setSelectedQuestionnaire(value);
                setQuestionnaireStatus("not_started");
                setQuestionnaireAnswers({});
                setQuestionnaireResponseId(null);
              }}
            >
              {questionnaires.map((questionnaire) => (
                <MenuItem key={questionnaire.id} value={questionnaire.id}>
                  {questionnaire.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedQuestionnaire && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Chip
                label={
                  questionnaireStatus === "not_started"
                    ? "Questionnaire Not Started"
                    : questionnaireStatus === "incomplete"
                    ? "Questionnaire Incomplete"
                    : "Questionnaire Complete"
                }
                color={
                  questionnaireStatus === "complete"
                    ? "success"
                    : questionnaireStatus === "incomplete"
                    ? "warning"
                    : "default"
                }
              />
              <Button
                variant="outlined"
                onClick={handleOpenQuestionnaireModal}
                disabled={!selectedQuestionnaire}
              >
                {questionnaireStatus === "not_started"
                  ? "Start Questionnaire"
                  : questionnaireStatus === "incomplete"
                  ? "Continue Questionnaire"
                  : "Edit Questionnaire"}
              </Button>
              <Button onClick={handleClearQuestionnaire} size="small">
                Clear
              </Button>
            </Box>
          )}
          <TextField
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            sx={styles.textField}
          />
          <Box sx={styles.buttonContainer}>
            <Button onClick={handleClose} sx={styles.cancelButton}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={styles.submitButton}
              disabled={
                selectedQuestionnaire
                  ? questionnaireStatus !== "complete"
                  : false
              }
            >
              Add Activity
            </Button>
          </Box>
        </Box>
      </Modal>
      {selectedQuestionnaire && (
        <QuestionnaireModal
          key={questionnaireModalKey}
          open={isQuestionnaireModalOpen}
          onClose={handleCloseQuestionnaireModal}
          memberId={memberId}
          questionnaireId={selectedQuestionnaire}
          responseId={questionnaireResponseId}
          isActivityContext={true}
          onQuestionnaireChange={handleQuestionnaireChange}
          refreshMembers={refreshMembers}
          currentAnswers={questionnaireAnswers}
          loadAnswers={false}
        />
      )}
    </>
  );
};

export default AddActivityModal;
